import { Company } from 'module/api/company/types';
import { useParams } from '@rocketlane/rocket-router';
import { Document } from 'api/pages';
import { MeetingPage } from 'api/pages/types';
import { useGetDocumentFeedbackQuery } from 'queries/feedback';
import { useGetDocumentQuery } from 'queries/pages';
import { useCallback, useEffect } from 'react';
import { useMeetingPagesRouteNavigations } from './route-navigations';

interface Props {
  company: Company;
  meetingPages: MeetingPage[];
  isMeetingPagesLoading: boolean;
  projectId?: number;
}
interface Result {
  meetingPage?: Document;
  isMeetingPageLoading: boolean;
}
export const useCurrentMeetingPage = ({
  company,
  meetingPages,
  isMeetingPagesLoading,
  projectId,
}: Props): Result => {
  const { meetingId: meetingIdParam, meetingPageId: meetingPageIdParam } =
    useParams<{ meetingId?: string; meetingPageId?: string }>();
  const { navigateToMeeting } = useMeetingPagesRouteNavigations(projectId);

  // Use meetingId if available, otherwise use meetingPageIdParam
  const documentIdParam = meetingIdParam ?? meetingPageIdParam;

  const meetingId = Number(documentIdParam);
  const isMeetingIdValid = !isNaN(meetingId);
  const { data: meetingDocument, isLoading: isMeetingDocumentLoading } =
    useGetDocumentQuery(isMeetingIdValid ? meetingId : -1, {
      enabled: isMeetingIdValid,
      retry: false,
    });

  const { isLoading: isFeedbackLoading } = useGetDocumentFeedbackQuery(
    'DOCUMENT',
    isMeetingIdValid ? meetingId.toString() : '-1',
    {
      enabled: isMeetingIdValid,
      retry: false,
    },
  );
  const navigateToFirstMeetingPage = useCallback(() => {
    if (meetingPages.length > 0) {
      const companyIdForNavigation = projectId ?? company.companyId;

      navigateToMeeting(companyIdForNavigation, meetingPages[0]?.documentId);
    }
  }, [navigateToMeeting, company.companyId, meetingPages, projectId]);

  useEffect(() => {
    if (isMeetingPagesLoading) {
      return;
    }
    if (!isMeetingIdValid) {
      navigateToFirstMeetingPage();
    }
  }, [isMeetingPagesLoading, isMeetingIdValid, navigateToFirstMeetingPage]);
  useEffect(() => {
    if (isMeetingDocumentLoading || isMeetingPagesLoading) {
      return;
    }
    if (isMeetingIdValid && meetingDocument == null) {
      navigateToFirstMeetingPage();
    }
  }, [
    isMeetingDocumentLoading,
    isMeetingPagesLoading,
    isMeetingIdValid,
    meetingDocument,
    navigateToFirstMeetingPage,
  ]);

  return {
    meetingPage: meetingDocument,
    isMeetingPageLoading: isMeetingDocumentLoading || isFeedbackLoading,
  };
};
